import { useCallback } from 'react';
import {
  trackEvent,
  trackFacebookEvent,
  trackLinkedInEvent,
  trackHotjarEvent,
  trackPurchaseComplete,
  trackFormationViewDetails,
  trackFacebookLead,
  trackLinkedInConversion,
  trackHotjarFormSubmit
} from '@/utils/tracking';

/**
 * Hook personnalisé pour faciliter le tracking marketing
 * Combine Google Analytics, Facebook Pixel, LinkedIn et Hotjar
 */
export const useMarketingTracking = () => {
  
  // Tracking des formations
  const trackFormationInteraction = useCallback((action: string, formationId: string, formationName: string) => {
    switch (action) {
      case 'view':
        trackFormationViewDetails(formationId, formationName);
        break;
      case 'add_to_cart':
        trackEvent('add_to_cart', { item_id: formationId, item_name: formationName });
        trackFacebookEvent('AddToCart', { content_ids: [formationId], content_name: formationName });
        trackHotjarEvent(`add_to_cart_${formationId}`);
        break;
      case 'begin_checkout':
        trackEvent('begin_checkout', { item_id: formationId, item_name: formationName });
        trackFacebookEvent('InitiateCheckout', { content_ids: [formationId], content_name: formationName });
        trackHotjarEvent(`begin_checkout_${formationId}`);
        break;
      case 'purchase':
        // Sera géré par trackPurchaseComplete avec plus de détails
        break;
    }
  }, []);

  // Tracking des formulaires
  const trackFormInteraction = useCallback((formName: string, action: string) => {
    switch (action) {
      case 'start':
        trackEvent('form_start', { form_name: formName });
        trackHotjarEvent(`form_start_${formName}`);
        break;
      case 'submit':
        trackEvent('form_submit', { form_name: formName });
        trackFacebookLead(formName);
        trackHotjarFormSubmit(formName);
        break;
      case 'error':
        trackEvent('form_error', { form_name: formName });
        trackHotjarEvent(`form_error_${formName}`);
        break;
    }
  }, []);

  // Tracking des CTA et boutons
  const trackCTAClick = useCallback((ctaName: string, location: string) => {
    trackEvent('cta_click', { cta_name: ctaName, location });
    trackFacebookEvent('Contact', { content_name: ctaName });
    trackHotjarEvent(`cta_click_${ctaName}`);
  }, []);

  // Tracking des téléchargements
  const trackDownload = useCallback((fileName: string, fileType: string) => {
    trackEvent('file_download', { file_name: fileName, file_type: fileType });
    trackFacebookEvent('Download', { content_name: fileName });
    trackHotjarEvent(`download_${fileType}`);
  }, []);

  // Tracking des vidéos
  const trackVideoInteraction = useCallback((videoName: string, action: string, progress?: number) => {
    const eventData: any = { video_name: videoName, action };
    if (progress !== undefined) {
      eventData.progress = progress;
    }
    
    trackEvent('video_interaction', eventData);
    trackHotjarEvent(`video_${action}_${videoName}`);
  }, []);

  // Tracking des recherches
  const trackSearch = useCallback((searchTerm: string, resultsCount: number) => {
    trackEvent('search', { search_term: searchTerm, results_count: resultsCount });
    trackFacebookEvent('Search', { search_string: searchTerm });
    trackHotjarEvent('search_performed');
  }, []);

  // Tracking de l'engagement utilisateur
  const trackEngagement = useCallback((engagementType: string, details?: any) => {
    trackEvent('user_engagement', { engagement_type: engagementType, ...details });
    trackHotjarEvent(`engagement_${engagementType}`);
  }, []);

  return {
    // Formations
    trackFormationInteraction,
    
    // Formulaires
    trackFormInteraction,
    
    // CTA et boutons
    trackCTAClick,
    
    // Téléchargements
    trackDownload,
    
    // Vidéos
    trackVideoInteraction,
    
    // Recherche
    trackSearch,
    
    // Engagement
    trackEngagement,
    
    // Fonctions avancées
    trackPurchaseComplete,
    trackLinkedInConversion,
    
    // Fonctions brutes pour cas spéciaux
    trackEvent,
    trackFacebookEvent,
    trackLinkedInEvent,
    trackHotjarEvent
  };
};